import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import useGameRoom from '../hooks/useGameRoom';
import AuthModal from '../components/AuthModal';
import LanguageDropdown from '../components/LanguageDropdown';

const Container = styled.div`
  min-height: 100vh;
  background: var(--color-light-bg);
  color: var(--color-dark);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const Logo = styled(motion.h1)`
  font-size: 4rem;
  margin-bottom: 1rem;
  text-align: center;
  color: var(--color-dark);
  text-shadow: 3px 3px 6px rgba(0,0,0,0.7);
  font-weight: 800;
  font-family: 'Fredoka', Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
    margin-bottom: 0.8rem;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  text-align: center;
  color: var(--color-dark);
  font-family: 'Fredoka', Arial, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 3rem;
  text-align: center;
  color: var(--color-dark);
  max-width: 600px;
  line-height: 1.6;
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 15px 40px 0 rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.8);
  max-width: 400px;
  width: 100%;
  color: var(--color-dark);
  
  @media (max-width: 768px) {
    padding: 30px;
    max-width: 350px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
    max-width: 300px;
    border-radius: 15px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 400px;
`;

const Input = styled.input`
  padding: 15px 20px;
  font-size: 1rem;
  border: 2px solid var(--color-secondary);
  border-radius: 12px;
  background: var(--color-light-bg);
  color: var(--color-dark);
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
  font-family: 'Nunito', Arial, sans-serif;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    background: var(--color-light-bg);
  }
  
  &::placeholder {
    color: var(--color-dark);
    opacity: 0.6;
  }
`;

const Button = styled(motion.button)`
  padding: 15px 30px;
  font-size: 1.1rem;
  font-weight: 700;
  border: ${props => props.isSecondary ? '2px solid var(--color-secondary)' : 'none'};
  border-radius: 12px;
  cursor: pointer;
  background: ${props => props.isSecondary ? 'var(--color-light-bg)' : 'var(--color-primary)'};
  color: ${props => props.isSecondary ? 'var(--color-dark)' : 'var(--color-light-bg)'};
  margin: 0.5rem;
  min-width: 200px;
  transition: all 0.3s ease;
  font-family: 'Fredoka', Arial, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(247, 5, 108, 0.3);
    background: ${props => props.isSecondary ? 'var(--color-secondary)' : 'var(--color-accent)'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-primary);
  margin-top: 1rem;
  text-align: center;
  font-weight: 600;
`;

const Section = styled.div`
  margin: 2rem 0;
  padding: 2rem;
  border-bottom: 2px solid var(--color-secondary);
  width: 100%;
  max-width: 800px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--color-primary);
  font-family: 'Fredoka', Arial, sans-serif;
  font-weight: 700;
  text-transform: uppercase;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: var(--color-secondary);
  font-weight: 600;
  font-size: 16px;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 2px solid var(--color-secondary);
  }
  
  &::before {
    margin-right: 15px;
  }
  
  &::after {
    margin-left: 15px;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255,255,255,.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LanguageSwitcher = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 999;
  
  @media (max-width: 768px) {
    top: 75px;
    right: 15px;
  }
  
  @media (max-width: 480px) {
    top: 120px;
    right: 10px;
  }
`;

const UserSection = styled.div`
  position: fixed;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  z-index: 1000;
  
  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    left: 10px;
    flex-direction: column;
    gap: 8px;
  }
`;

const PremiumSection = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  z-index: 1001;
  
  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    top: 10px;
    right: 10px;
    flex-direction: column;
    gap: 8px;
  }
`;

const PremiumButton = styled(motion.button)`
  background: linear-gradient(45deg, #ffd700, #ffb347);
  color: var(--color-dark);
  border: none;
  padding: 12px 20px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 10px 16px;
  }
`;

const CartButton = styled(motion.button)`
  position: relative;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--color-dark);
  padding: 10px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 8px 12px;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: var(--color-accent);
  color: var(--color-dark);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
`;

const UserInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 10px 15px;
  border-radius: 25px;
  color: var(--color-dark);
  font-size: 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 8px 12px;
  }
`;

const UserAvatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(45deg, #4ecdc4, #45b7d1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-dark);
  font-weight: bold;
  font-size: 0.8rem;
`;

const UserButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--color-dark);
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 6px 12px;
  }
`;

const AuthButton = styled(motion.button)`
  background: linear-gradient(45deg, #4ecdc4, #45b7d1);
  color: var(--color-dark);
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(78, 205, 196, 0.3);
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 10px 20px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const HomePage = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { user, loading: authLoading, logout, isPremium } = useAuth();
  const { cartItemCount, setIsCartOpen } = useCart();
  const [roomCode, setRoomCode] = useState('');
  const [createAttempted, setCreateAttempted] = useState(false);
  const [createdRoomCode, setCreatedRoomCode] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isPrivateRoom, setIsPrivateRoom] = useState(false);
  const [roomPassword, setRoomPassword] = useState('');
  const { createRoom, error, loading } = useGameRoom(null);

  useEffect(() => {
    if (createdRoomCode) {
      const timer = setTimeout(() => {
        console.log('Navigating to room:', createdRoomCode);
        navigate(`/room/${createdRoomCode}`);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [createdRoomCode, navigate]);

  const handleCreateRoom = async () => {
    // Check if user is logged in
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (loading) {
      console.log('Room creation already in progress, skipping...');
      return;
    }
    
    try {
      setCreateAttempted(true);
      console.log('Creating new room...');
      
      // Add a timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Room creation timed out')), 15000);
      });
      
      const newRoomCode = await Promise.race([createRoom(), timeoutPromise]);
      
      if (newRoomCode) {
        console.log('Room created successfully:', newRoomCode);
        setCreatedRoomCode(newRoomCode);
      } else {
        console.error('Failed to create room: No room code returned');
      }
    } catch (err) {
      console.error('Error in handleCreateRoom:', err);
    }
  };

  const handleJoinRoom = async (e) => {
    e.preventDefault();
    if (!roomCode.trim() || loading) {
      return;
    }
    
    navigate(`/room/${roomCode.toUpperCase()}`);
  };

  const handleGoToPremium = () => {
    navigate('/payment');
  };

  return (
    <Container>
      {/* User Authentication Section */}
      <UserSection>
        {user ? (
          <>
            <UserInfo>
              <UserAvatar>
                {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
              </UserAvatar>
              <span>Hi, {user.full_name || 'User'}!</span>
            </UserInfo>
            <UserButton onClick={logout}>Logout</UserButton>
          </>
        ) : (
          <AuthButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAuthModal(true)}
          >
            Sign In / Sign Up
          </AuthButton>
        )}
      </UserSection>

      {/* Premium & Cart Section */}
      <PremiumSection>
        {!isPremium && (
          <PremiumButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleGoToPremium}
          >
            ⭐ Go Premium
          </PremiumButton>
        )}
        
        <CartButton
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsCartOpen(true)}
        >
          🛒 Cart
          {cartItemCount > 0 && (
            <CartBadge>{cartItemCount}</CartBadge>
          )}
        </CartButton>
      </PremiumSection>

      <LanguageSwitcher>
        <LanguageDropdown />
      </LanguageSwitcher>
      <div style={{ width: '100%', textAlign: 'center', margin: '32px 0 16px 0' }}>
        <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Parteezy Logo" style={{ height: 120, maxWidth: '90vw', marginBottom: 8 }} />
      </div>
      
      <Subtitle
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        {t('subtitle')}
      </Subtitle>
      
      <Card
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {/* Private Room Options */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            marginBottom: '15px',
            opacity: !isPremium ? 0.6 : 1
          }}>
            <input
              type="checkbox"
              id="privateRoom"
              checked={isPrivateRoom}
              onChange={(e) => {
                if (!isPremium) {
                  alert(t('privateRoomAlert'));
                  return;
                }
                setIsPrivateRoom(e.target.checked);
                if (!e.target.checked) {
                  setRoomPassword('');
                }
              }}
              disabled={!isPremium}
              style={{ 
                width: '18px', 
                height: '18px',
                cursor: isPremium ? 'pointer' : 'not-allowed'
              }}
            />
            <label 
              htmlFor="privateRoom" 
              style={{ 
                fontSize: '16px', 
                fontWeight: '500',
                cursor: isPremium ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              🔒 {t('privateRoom')}
              {!isPremium && <span style={{ color: '#ff6b6b', fontSize: '12px' }}>{t('premium')}</span>}
            </label>
          </div>
          
          {isPrivateRoom && isPremium && (
            <Input
              type="password"
              placeholder={t('enterRoomPassword')}
              value={roomPassword}
              onChange={(e) => setRoomPassword(e.target.value)}
              style={{ marginBottom: '0' }}
            />
          )}
        </div>

        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleCreateRoom}
          disabled={loading || (isPrivateRoom && !roomPassword.trim())}
        >
          {loading ? (
            <>
              <LoadingSpinner /> {t('creatingRoom')}
            </>
          ) : (
            isPrivateRoom ? t('createPrivateRoom') : t('createRoom')
          )}
        </Button>
        
        <Divider>{language === 'nl' ? 'of' : 'or'}</Divider>
        
        <Form onSubmit={handleJoinRoom}>
          <Input
            type="text"
            placeholder={t('enterRoomCode')}
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value)}
            disabled={loading}
          />
          <Button 
            type="submit"
            isSecondary={true}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading || !roomCode.trim()}
          >
            {t('joinRoom')}
          </Button>
        </Form>
        
        {error && createAttempted && (
          <ErrorMessage>
            {error}
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {error.includes('6 active rooms') || error.includes('Room limit') ? (
                <button 
                  onClick={() => navigate('/manage-rooms')}
                  style={{ 
                    background: 'rgba(78, 205, 196, 0.8)', 
                    border: 'none', 
                    padding: '8px 15px', 
                    borderRadius: '5px', 
                    color: 'white', 
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {t('manageRooms')}
                </button>
              ) : null}
              <button 
                onClick={() => {
                  setCreateAttempted(false);
                }}
                style={{ 
                  background: 'rgba(255,255,255,0.2)', 
                  border: 'none', 
                  padding: '8px 15px', 
                  borderRadius: '5px', 
                  color: 'white', 
                  cursor: 'pointer' 
                }}
              >
                {language === 'nl' ? 'Probeer Opnieuw' : 'Try Again'}
              </button>
            </div>
          </ErrorMessage>
        )}
        
        {user && (
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <Button
              isSecondary={true}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/manage-rooms')}
              style={{ fontSize: '14px', padding: '10px 20px' }}
            >
              🏠 {t('manageRooms')}
            </Button>
          </div>
        )}
      </Card>
      
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        fontSize: '0.75rem',
        color: 'rgba(255, 255, 255, 0.4)',
        fontWeight: '300',
        letterSpacing: '0.5px',
        userSelect: 'none',
        zIndex: 1000
      }}>
        {t('copyright')}
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="signup"
      />
    </Container>
  );
};

export default HomePage; 